import { memo, useCallback, useState, useMemo } from 'react';
import {
    Layout,
    Sparkles,
    Wand2,
    Info,
    Plus,
    Trash2,
    Cloud,
    Newspaper,
    MessageSquare,
    Share2,
    Heart
} from 'lucide-react';
import { DEFAULT_TEMPLATE_ID, VIDEO_TEMPLATES, templatePreviewHasVignette } from '../data/templates';
import { useTemplateActions } from '../stores/useTemplateActions';
import { useEditorStore } from '../stores/editor-store';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/features/i18n/locales';
import { useSettingsStore } from '@/features/settings/stores/settings-store';
import { useTemplateApplyStore } from '../stores/template-apply-store';

function resolveDefaultCategory(): 'basic' | 'issue' | 'ssul' | 'sns' | 'my' {
    const defaultTemplate = VIDEO_TEMPLATES.find((template) => template.id === DEFAULT_TEMPLATE_ID);

    if (defaultTemplate?.layoutId === 'ssul') return 'ssul';
    if (defaultTemplate?.layoutId?.startsWith('issue')) return 'issue';
    if (defaultTemplate?.layoutId === 'sns' || defaultTemplate?.id.includes('sns')) return 'sns';
    return 'basic';
}

export const TemplatesPanel = memo(function TemplatesPanel() {
    const t = useTranslation();
    const {
        applyTemplate,
        syncCurrentStyleToAll,
        saveCurrentAsTemplate,
        deleteCustomTemplate,
        registerCurrentToCloud
    } = useTemplateActions();

    const customTemplates = useEditorStore(s => s.customTemplates);
    const cloudTemplates = useEditorStore(s => s.cloudTemplates);
    const activeTemplateId = useEditorStore(s => s.activeTemplateId);
    const templateOptions = useEditorStore(s => s.templateOptions);
    const setTemplateOptions = useEditorStore(s => s.setTemplateOptions);
    const isApplyingTemplate = useTemplateApplyStore(s => s.isApplying);

    const [categoryTab, setCategoryTab] = useState<'basic' | 'issue' | 'ssul' | 'sns' | 'my'>(resolveDefaultCategory);

    const tabs = [
        { id: 'basic', label: t.templates.tabBasic, icon: Layout },
        { id: 'issue', label: t.templates.tabIssue, icon: Newspaper },
        { id: 'ssul', label: t.templates.tabSsul, icon: MessageSquare },
        { id: 'sns', label: t.templates.tabSns, icon: Share2 },
        { id: 'my', label: t.templates.tabMy, icon: Heart },
    ] as const;

    const handleApply = useCallback((templateId: string) => {
        void applyTemplate(templateId);
    }, [applyTemplate]);

    const handleToggleDefaultVignette = useCallback(() => {
        const nextEnabled = !templateOptions.vignetteEnabled;
        setTemplateOptions({ vignetteEnabled: nextEnabled });
        const templateIdToRefresh = activeTemplateId || DEFAULT_TEMPLATE_ID;

        queueMicrotask(() => {
            void applyTemplate(templateIdToRefresh);
        });
    }, [activeTemplateId, applyTemplate, setTemplateOptions, templateOptions.vignetteEnabled]);

    const filteredTemplates = useMemo(() => {
        if (categoryTab === 'my') return [...cloudTemplates, ...customTemplates];

        let templates = VIDEO_TEMPLATES;

        return templates.filter(t => {
            if (categoryTab === 'basic') return t.layoutId === 'basic';
            if (categoryTab === 'issue') return t.layoutId?.startsWith('issue');
            if (categoryTab === 'ssul') return t.layoutId === 'ssul';
            if (categoryTab === 'sns') return t.layoutId === 'sns' || t.id.includes('sns');
            return false;
        });
    }, [categoryTab, customTemplates, cloudTemplates]);

    return (
        <div className="h-full flex flex-col bg-background">
            {/* Category Tabs */}
            <div className="grid grid-cols-5 gap-1 p-2 border-b border-border bg-secondary/10">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setCategoryTab(tab.id)}
                        className={cn(
                            "flex flex-col items-center justify-center py-2 rounded-lg transition-all relative",
                            categoryTab === tab.id
                                ? "bg-primary text-primary-foreground shadow-md ring-1 ring-primary"
                                : "text-muted-foreground hover:bg-secondary/50"
                        )}
                    >
                        <tab.icon className={cn(
                            "w-4 h-4 mb-1",
                            categoryTab === tab.id ? "text-primary-foreground" : "text-muted-foreground"
                        )} />
                        <span className="text-[10px] font-bold tracking-tight">{tab.label}</span>
                        {categoryTab === tab.id && (
                            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-primary ring-2 ring-background" />
                        )}
                    </button>
                ))}
            </div>

            {/* Action Buttons (Sticky) */}
            <div className="p-3 border-b border-border space-y-2 bg-background/50 backdrop-blur-sm">
                <button
                    onClick={() => syncCurrentStyleToAll()}
                    className={cn(
                        "w-full py-2 px-4 rounded-lg flex items-center justify-center gap-2",
                        "bg-primary text-primary-foreground font-bold text-xs uppercase tracking-tight",
                        "hover:bg-primary/90 active:scale-[0.98] transition-all shadow-md"
                    )}
                >
                    <Wand2 className="w-3.5 h-3.5" />
                    {t.templates.applyToAll}
                </button>

                <div className="grid grid-cols-2 gap-2">
                    <button
                        onClick={() => saveCurrentAsTemplate()}
                        className={cn(
                            "py-1.5 px-2 rounded-lg flex items-center justify-center gap-1.5",
                            "bg-secondary text-secondary-foreground text-[10px] font-bold border border-border",
                            "hover:bg-secondary/80 active:scale-[0.98] transition-all"
                        )}
                    >
                        <Plus className="w-3 h-3 text-primary" />
                        {t.templates.saveMyTemplate}
                    </button>
                    <button
                        onClick={() => registerCurrentToCloud()}
                        className={cn(
                            "py-1.5 px-2 rounded-lg flex items-center justify-center gap-1.5",
                            "bg-secondary text-secondary-foreground text-[10px] font-bold border border-border",
                            "hover:bg-secondary/80 active:scale-[0.98] transition-all"
                        )}
                    >
                        <Cloud className="w-3 h-3 text-primary" />
                        {t.templates.registerCloud}
                    </button>
                </div>

                <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-secondary/20 px-3 py-2">
                    <div className="min-w-0">
                        <div className="text-[10px] font-bold text-foreground">비네팅 자동 적용</div>
                        <div className="text-[9px] leading-snug text-muted-foreground">
                            기본 템플릿 마지막 프리셋에만 가장자리 어둡게 효과를 자동으로 넣습니다.
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={handleToggleDefaultVignette}
                        className={cn(
                            'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors',
                            templateOptions.vignetteEnabled
                                ? 'border-primary bg-primary'
                                : 'border-border bg-muted'
                        )}
                        aria-pressed={templateOptions.vignetteEnabled}
                        aria-label="비네팅 자동 적용"
                    >
                        <span
                            className={cn(
                                'inline-block h-4 w-4 rounded-full bg-white transition-transform',
                                templateOptions.vignetteEnabled ? 'translate-x-6' : 'translate-x-1'
                            )}
                        />
                    </button>
                </div>
            </div>

            {/* Presets Grid */}
            <div className="flex-1 overflow-y-auto p-2 custom-scrollbar">
                <div className="mb-4">
                    <div className="text-[10px] font-black text-muted-foreground/50 uppercase tracking-widest mb-3 flex items-center justify-between px-1">
                        <span>{categoryTab} {t.templates.presetsTitle}</span>
                        <span className="text-[9px] bg-secondary/50 px-1.5 py-0.5 rounded tabular-nums">
                            {filteredTemplates.length}
                        </span>
                    </div>

                    <div className="grid grid-cols-4 gap-1.5">
                        {filteredTemplates.map((template) => (
                            <div key={template.id} className="relative group/item">
                                <button
                                    onClick={() => handleApply(template.id)}
                                    disabled={isApplyingTemplate}
                                    className={cn(
                                        'w-full flex flex-col items-center gap-1 p-0.5 rounded-md',
                                        'border border-border bg-secondary/10',
                                        'hover:bg-secondary/30 hover:border-primary/40',
                                        'transition-all duration-200 group text-center relative overflow-hidden ring-offset-background',
                                        isApplyingTemplate && 'opacity-60 pointer-events-none'
                                    )}
                                >
                                    {/* Visual Mini Preview (Thumbnail) */}
                                    <div className="w-full aspect-[9/16] rounded-sm overflow-hidden bg-black/40 relative shadow-sm border border-white/5 group-hover:scale-[1.02] transition-transform duration-300">
                                        {/* Background Representation */}
                                        <div
                                            className="absolute inset-0 opacity-80"
                                            style={{
                                                backgroundColor: template.background?.type === 'color' ? template.background.value : '#111',
                                                backgroundImage: template.background?.type === 'image' ? `url(${template.background.value})` : 'none',
                                                backgroundSize: 'cover'
                                            }}
                                        />
                                        {templatePreviewHasVignette(template, templateOptions) && (
                                            <div
                                                className="absolute inset-0 pointer-events-none"
                                                style={{
                                                    background: 'radial-gradient(ellipse at center, rgba(0, 0, 0, 0) 38%, rgba(0, 0, 0, 0.22) 68%, rgba(0, 0, 0, 0.52) 100%)',
                                                }}
                                            />
                                        )}

                                        {/* Layout Content Box */}
                                        <div
                                            className="absolute bg-white/10 border border-white/20 backdrop-blur-[1px] rounded-[1px]"
                                            style={{
                                                left: `calc(50% + ${(template.layout.x / 1080) * 100}%)`,
                                                top: `calc(50% + ${(template.layout.y / 1920) * 100}%)`,
                                                width: `${(template.layout.width || 1080) / 1080 * 100}%`,
                                                height: `${(template.layout.height || 1920) / 1920 * 100}%`,
                                                transform: 'translate(-50%, -50%)',
                                            }}
                                        />

                                        {/* Title 1 Representation */}
                                        {template.title && (
                                            <div
                                                className="absolute rounded-[0.5px]"
                                                style={{
                                                    left: `calc(50% + ${(template.title.position.x / 1080) * 100}%)`,
                                                    top: `calc(50% + ${(template.title.position.y / 1920) * 100}%)`,
                                                    width: '75%',
                                                    height: '4%',
                                                    backgroundColor: template.title.style.color || '#fff',
                                                    boxShadow: template.title.style.backgroundColor ? `0 0 0 1px ${template.title.style.backgroundColor}` : 'none',
                                                    transform: 'translate(-50%, -50%)',
                                                    opacity: 0.9,
                                                }}
                                            />
                                        )}

                                        {/* Title 2 Representation */}
                                        {template.title?.isDoubleLine && (
                                            <div
                                                className="absolute rounded-[0.5px]"
                                                style={{
                                                    left: `calc(50% + ${(template.title.secondaryPosition?.x ?? 0) / 1080 * 100}%)`,
                                                    top: `calc(50% + ${(template.title.secondaryPosition?.y ?? (template.title.position.y + 150)) / 1920 * 100}%)`,
                                                    width: '65%',
                                                    height: '3.5%',
                                                    backgroundColor: template.title.secondaryStyle?.color || template.title.style.secondaryColor || template.title.style.color || '#fff',
                                                    boxShadow: template.title.secondaryStyle?.backgroundColor ? `0 0 0 1px ${template.title.secondaryStyle.backgroundColor}` : 'none',
                                                    transform: 'translate(-50%, -50%)',
                                                    opacity: 0.9,
                                                }}
                                            />
                                        )}



                                        {/* Caption Representation */}
                                        {template.textStyle && (
                                            <div
                                                className="absolute rounded-[0.5px]"
                                                style={{
                                                    left: `calc(50% + ${((template.textStyle as any).x ?? 0) / 1080 * 100}%)`,
                                                    top: `${(() => {
                                                        const y = template.textStyle!.captionY ?? 0.8;
                                                        if (y > -1 && y < 1.1) return y * 100;
                                                        return ((960 + y) / 1920) * 100;
                                                    })()}%`,
                                                    width: '55%',
                                                    height: '2.5%',
                                                    backgroundColor: template.textStyle.color || '#ff0',
                                                    boxShadow: template.textStyle.backgroundColor ? `0 0 0 1px ${template.textStyle.backgroundColor}` : 'none',
                                                    transform: 'translate(-50%, -50%)',
                                                }}
                                            />
                                        )}

                                        {/* Active Indicator */}
                                        {activeTemplateId === template.id && (
                                            <div className="absolute inset-0 ring-2 ring-primary ring-inset z-10" />
                                        )}
                                    </div>

                                    <div className="w-full flex flex-col items-center">
                                        <div className="font-bold text-[8px] text-foreground/70 line-clamp-1 truncate w-full group-hover:text-primary transition-colors px-0.5">
                                            {template.name.replace(`${categoryTab.charAt(0).toUpperCase() + categoryTab.slice(1)} - `, '')}
                                        </div>
                                    </div>

                                    {/* Hover Details Popover (Simplified) */}
                                    <div className="absolute inset-0 bg-primary/10 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                                </button>

                                {categoryTab === 'my' && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (confirm('이 템플릿을 삭제할까요?')) {
                                                deleteCustomTemplate(template.id);
                                            }
                                        }}
                                        className="absolute top-1 right-1 p-1 rounded-md bg-destructive/80 text-white hover:bg-destructive opacity-0 group-hover/item:opacity-100 transition-all shadow-lg z-20"
                                    >
                                        <Trash2 className="w-2.5 h-2.5" />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>

                    {filteredTemplates.length === 0 && (
                        <div className="py-20 flex flex-col items-center justify-center text-center px-4">
                            <div className="w-10 h-10 rounded-full bg-secondary/50 flex items-center justify-center mb-4">
                                <Sparkles className="w-5 h-5 text-muted-foreground/30" />
                            </div>
                            <h3 className="text-[11px] font-bold text-muted-foreground">{t.common.loading}</h3>
                        </div>
                    )}
                </div>
            </div>

            {/* Info Footer */}
            <div className="p-2 border-t border-border bg-secondary/5">
                <div className="flex items-start gap-2 text-[9px]">
                    <Info className="w-3 h-3 text-primary flex-shrink-0 mt-0.5" />
                    <div className="text-muted-foreground leading-snug">
                        <span className="text-primary font-bold">{t.templates.applyToAll.split('(')[0]}:</span>
                        <br />
                        {useSettingsStore.getState().language === 'ko' ? '모든 클립에 이 스타일을 즉시 덮어씌웁니다.' : 'Instantly overrides all clips with this style.'}
                    </div>
                </div>
            </div>
        </div>
    );
});
